'use client';

/**
 * TestDashboardWidget - Test Statistics Dashboard Canvas
 *
 * @component_role Test Results Visualization
 * @business_rule Displays aggregate test statistics:
 *   - Big numbers: total runs, pass rate, avg duration
 *   - Category breakdown with pass/fail bars
 *   - Recent failures list (clickable to navigate)
 *   - Flaky tests warning section
 * @user_outcome See test health at a glance, identify problem areas quickly
 * @integration_point klyde_edge /testing/dashboard endpoint
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  BarChart3,
  Loader2,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// ---------------------------------------------------------------------------
// Types (aligned with router.py DashboardResponse)
// ---------------------------------------------------------------------------

interface DashboardStats {
  total_runs: number;
  cases_executed: number;
  total_executions: number;
  passed: number;
  failed: number;
  pass_rate: number;
  avg_duration_ms: number | null;
}

interface CategoryPassRate {
  category: string;
  total: number;
  passed: number;
  failed: number;
  pass_rate: number;
}

interface TriggerStats {
  trigger: string;
  run_count: number;
  avg_pass_rate: number | null;
}

interface FailureRecord {
  case_id: string;
  case_name: string;
  category: string | null;
  summary: string | null;
  run_id: string;
  timestamp: string | null;
  trigger: string | null;
}

interface FlakyTest {
  case_id: string;
  name: string;
  category: string | null;
  passes: number;
  failures: number;
  total_runs: number;
  pass_rate: number;
}

interface DashboardResponse {
  period_hours: number;
  stats: DashboardStats;
  by_category: CategoryPassRate[];
  by_trigger: TriggerStats[];
  recent_failures: FailureRecord[];
  flaky_tests: FlakyTest[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TestDashboardWidgetProps {
  entityId?: string;
  title?: string;
}

export function TestDashboardWidget({ title }: TestDashboardWidgetProps) {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodHours, setPeriodHours] = useState(24);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  // Fetch dashboard data
  const fetchDashboard = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<DashboardResponse>(
        `/testing/dashboard?hours=${periodHours}`
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [periodHours]);

  // Navigate to test case when clicking a failure
  const handleSelectCase = (caseId: string, caseName: string) => {
    selectNode({
      id: caseId,
      type: 'test_case',
      label: caseName,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button size="sm" variant="outline" onClick={fetchDashboard}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No test data available</p>
          <p className="text-xs mt-1">Run some tests to see statistics</p>
        </div>
      </div>
    );
  }

  const stats = data.stats;
  const passRateColor = stats.pass_rate >= 90 ? 'text-green-500' :
                        stats.pass_rate >= 70 ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium">
            {title || 'Test Dashboard'}
          </span>
          <Badge variant="outline" className="text-[10px]">
            Last {data.period_hours}h
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex items-center bg-muted/50 rounded-md p-0.5">
            {[24, 72, 168].map((hours) => (
              <button
                key={hours}
                onClick={() => setPeriodHours(hours)}
                className={cn(
                  'px-2 py-1 text-xs rounded transition-colors',
                  periodHours === hours
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {hours === 24 ? '24h' : hours === 72 ? '3d' : '7d'}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={fetchDashboard}
            className="h-7 w-7 p-0"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Big Numbers Row */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard
            icon={BarChart3}
            label="Total Runs"
            value={stats.total_runs}
            color="text-blue-400"
          />
          <StatCard
            icon={TrendingUp}
            label="Pass Rate"
            value={`${Math.round(stats.pass_rate)}%`}
            color={passRateColor}
          />
          <StatCard
            icon={CheckCircle2}
            label="Passed"
            value={stats.passed}
            subtitle={`of ${stats.total_executions}`}
            color="text-green-500"
          />
          <StatCard
            icon={Clock}
            label="Avg Duration"
            value={stats.avg_duration_ms ? `${Math.round(stats.avg_duration_ms)}ms` : '-'}
            color="text-muted-foreground"
          />
        </div>

        {/* Category Breakdown */}
        {data.by_category.length > 0 && (
          <section>
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              Pass Rate by Category
            </h3>
            <div className="space-y-2">
              {data.by_category.map((cat) => (
                <CategoryBar key={cat.category} data={cat} />
              ))}
            </div>
          </section>
        )}

        {/* Flaky Tests Warning */}
        {data.flaky_tests.length > 0 && (
          <section className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              Flaky Tests Detected
            </h3>
            <div className="space-y-1">
              {data.flaky_tests.slice(0, 5).map((test) => (
                <div
                  key={test.case_id}
                  className="flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-amber-500/10 cursor-pointer"
                  onClick={() => handleSelectCase(test.case_id, test.name)}
                >
                  <span className="truncate flex-1">{test.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {test.passes}/{test.total_runs} passed ({Math.round(test.pass_rate)}%)
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recent Failures */}
        {data.recent_failures.length > 0 && (
          <section>
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2 text-red-400">
              <XCircle className="w-4 h-4" />
              Recent Failures
            </h3>
            <div className="space-y-1 bg-muted/30 rounded-lg border border-border/50">
              {data.recent_failures.slice(0, 10).map((failure, i) => (
                <FailureRow
                  key={`${failure.case_id}-${i}`}
                  failure={failure}
                  onClick={() => handleSelectCase(failure.case_id, failure.case_name)}
                />
              ))}
            </div>
          </section>
        )}

        {/* No Failures Message */}
        {data.recent_failures.length === 0 && data.stats.total_runs > 0 && (
          <section className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-green-400">All tests passing</p>
          </section>
        )}

        {/* Trigger Stats */}
        {data.by_trigger.length > 0 && (
          <section>
            <h3 className="text-sm font-medium mb-3 text-muted-foreground">
              Runs by Trigger
            </h3>
            <div className="flex flex-wrap gap-2">
              {data.by_trigger.map((t) => (
                <Badge key={t.trigger} variant="secondary" className="text-xs">
                  {t.trigger}: {t.run_count} runs
                  {t.avg_pass_rate != null && ` (${Math.round(t.avg_pass_rate)}%)`}
                </Badge>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}

function StatCard({ icon: Icon, label, value, subtitle, color = 'text-foreground' }: StatCardProps) {
  return (
    <div className="bg-muted/30 border border-border/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn('w-4 h-4', color)} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className={cn('text-2xl font-bold', color)}>{value}</div>
      {subtitle && (
        <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
      )}
    </div>
  );
}

function CategoryBar({ data }: { data: CategoryPassRate }) {
  const passPercent = data.total > 0 ? (data.passed / data.total) * 100 : 0;
  const failPercent = data.total > 0 ? (data.failed / data.total) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm w-24 truncate" title={data.category}>
        {data.category}
      </span>
      <div className="flex-1 h-5 bg-muted/50 rounded overflow-hidden flex">
        <div
          className="bg-green-500/70 h-full transition-all"
          style={{ width: `${passPercent}%` }}
        />
        <div
          className="bg-red-500/70 h-full transition-all"
          style={{ width: `${failPercent}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-16 text-right">
        {data.passed}/{data.total}
      </span>
    </div>
  );
}

function FailureRow({
  failure,
  onClick,
}: {
  failure: FailureRecord;
  onClick: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 hover:bg-accent/50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
      onClick={onClick}
    >
      <XCircle className="w-4 h-4 text-red-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{failure.case_name}</div>
        {failure.summary && (
          <div className="text-xs text-muted-foreground truncate">
            {failure.summary}
          </div>
        )}
      </div>
      {failure.category && (
        <Badge variant="outline" className="text-[10px] shrink-0">
          {failure.category}
        </Badge>
      )}
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </div>
  );
}
